/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationsPackListPage Component
 * ------------------------
 * Displays all application packages.
 * Features:
 * 1. Fetches and lists application packages.
 * 2. Supports pagination, creation, and navigation to individual application package details.
 * 3. Provides error and loading states with user-friendly messages.
 *
 * Dependencies:
 * - APIHelper for backend API calls.
 * - ThemeContext for consistent UI theming.
 * - MessageBox, Pagination, and Button for UI elements.
 * - useAlertDialog for displaying confirmation/error dialogs.
 */

import React, { useEffect, useState } from 'react';
import ApplicationCard from '../components/ApplicationCard';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from "../config/envConfig";
import MessageBox from '../components/MessageBox';
import { useNavigate } from 'react-router-dom';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';
import Paginator from '../components/common/Paginator';
import { handleErrorResponse } from '../utils/utils';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ClientKeySecretDialog from '../components/common/ClientKeySecretDialog';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const perPage = 18;

export default function ApplicationsPackListPage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [installing, setInstalling] = useState(false);

    const { showAlertDialog } = useAlertDialog();

    const [cksOpen, setCksOpen] = useState(false);
    const [clientKey, setClientKey] = useState("");
    const [clientSecret, setClientSecret] = useState("");

    const [totalApplications, setTotalApplications] = useState(0);

    const packsQueryKey = ['applicationPacks', page, perPage];

    const packsQuery = useQuery({
        queryKey: packsQueryKey,
        queryFn: async ({ signal }) => {

            const url = `/arcee/application-pack/list/?page=${page}&page_size=${perPage}`;
            const resp = await apiHelper.apiCall(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal,
            });

            if (!resp.ok) {
                await handleErrorResponse(resp);
                throw new Error('Failed to load application packages');
            }

            return resp.json();
        },
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        retry: 1,
    });

    useEffect(() => {
        if (packsQuery.isLoading) {
            setLoading(true);
            setError(null);
            return;
        }

        if (packsQuery.isError) {
            setLoading(false);
            setApplications([]);
            setTotalApplications(0);
            setError(packsQuery.error?.message || 'Failed to fetch application packages');
            return;
        }

        if (packsQuery.data) {
            setApplications(packsQuery.data.results || []);
            setTotalApplications(packsQuery.data.count || 0);
            setLoading(false);
            setError(null);
        }
    }, [packsQuery.status, packsQuery.data, packsQuery.error]);

    const pageCount = Math.max(1, Math.ceil(totalApplications / perPage));
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
    };
    const handlePrevious = () => setPage(p => Math.max(p - 1, 1));
    const handleNext = () => setPage(p => Math.min(p + 1, pageCount));

    const handleOpen = (app) => { window.location = `/dashboard/marketplace/${app.id}`; };
    const handleEdit = (app) => { alert('Edit: ' + app.name); };
    const handleDelete = (app) => { if (window.confirm('Delete ' + app.name + '?')) { /* API delete */ } };

    const handleInstall = (app) => {
        showAlertDialog({
            title: "Confirm Installation",
            message: "Are you sure you want to install the package?",
            type: "info",
            okLabel: "Install",
            cancelLabel: "Cancel",
            showCancel: true,
            onOk: async () => {
                setInstalling(true);
                try {

                    const payload = {
                        application_pack_ids: [app.id],
                    };

                    const response = await apiHelper.apiCall('/arcee/application-pack/install/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errData = await response.json();
                        showAlertDialog({
                            title: "Installation Failed",
                            message: errData.message || response.statusText,
                            type: "error",
                            okLabel: "Retry",
                            cancelLabel: "Close",
                            showCancel: true,
                            onOk: () => handleInstall(app), // Retry installation
                            onCancel: () => window.location.reload(), // Close and reload
                        });
                        return;
                    }

                    const data = await response.json();
                    const installError = data.results.find(r => r.error);
                    if (installError) {
                        showAlertDialog({
                            title: "Installation Failed",
                            message: installError.error,
                            type: "error",
                            okLabel: "Retry",
                            cancelLabel: "Close",
                            showCancel: true,
                            onOk: () => handleInstall(app), // Retry installation
                            onCancel: () => window.location.reload(), // Close and reload
                        });
                        return;
                    }

                    const result = data.results[0];
                    if (result && result.client_id && result.client_secret) {
                        setClientKey(result.client_id);
                        setClientSecret(result.client_secret);
                        setCksOpen(true);
                    } else {
                        showAlertDialog({
                            title: "Success",
                            message: "Package installed successfully!",
                            type: "success",
                            okLabel: "Close",
                            showCancel: false,
                            onOk: () => window.location.reload(), // Close and reload
                        });
                    }
                } catch (error) {
                    console.error('Install error:', error);
                    showAlertDialog({
                        title: "Error",
                        message: "An error occurred during installation. Please try again later.",
                        type: "error",
                        okLabel: "Retry",
                        cancelLabel: "Close",
                        showCancel: true,
                        onOk: () => handleInstall(app), // Retry installation
                        onCancel: () => window.location.reload(), // Close and reload
                    });
                } finally {
                    setInstalling(false);
                }
            },
            onCancel: () => {
                // Do nothing on cancel
            }
        });
    };


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh', color: theme.primary }}>
                <span className="spinner-border" role="status" aria-hidden="true"></span>
                <span className="ms-2">Loading...</span>
            </div>
        );
    }

    if (error) {
        const displayError = error || "Something went wrong. Please try again.";
        return (
            <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
                <MessageBox messageType="error" message={displayError} />
            </div>
        );
    }

    if (applications.length === 0) {
        return (
            <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
                <MessageBox messageType="info" message="No Packages are present" />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column align-items-center px-2 py-5 min-vh-100" style={{ background: "#f7f8fb" }}>
            <h2 className="mb-5" style={{ color: theme.primary, fontWeight: 700, letterSpacing: '.06em' }}>
                Marketplace
            </h2>

            <div className="row w-100 gx-4 gy-5">
                {applications.map(app => (
                    <div key={app.id} className="col-12 col-md-6 col-lg-4 d-flex">
                        <ApplicationCard
                            app={{
                                ...app,
                                title: app.name,
                                state: app.status,
                                logoUrl: app.logo,
                                description: app.description,
                            }}
                            onOpen={handleOpen}
                            onInstall={() => handleInstall(app)}
                            showInstall={!app.is_owner}
                            showEdit={false}
                            showGenerateCredentials={false}
                            showDelete={false}
                            installed={app.installed}
                        />
                    </div>
                ))}
            </div>

            <Paginator
                page={page}
                pageCount={pageCount}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onPage={handlePageChange}
                maxPagesToShow={3}
            />

            <ProgressBarDialog
                open={installing}
                text_message="Installing package..."
                cancellable={false}
            />
            <ClientKeySecretDialog
                open={cksOpen}
                onClose={() => {
                    setCksOpen(false);
                    window.location.reload();
                }}
                clientKey={clientKey}
                clientSecret={clientSecret}
                canRegenerate={false}
                actionLabel="Close"
            />
        </div>
    );
}
